import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { Formik } from "formik";
import { ConnectedFocusError } from "focus-formik-error";
import ImageUploader from "../../../../../components/utilities/ImageUploader";
import { BCPCircleCheck } from "../../../../../components/icons/bcp/BCPIcons";
import GenericPicker from "../../../../../components/utilities/GenericPicker";
import LocationPicker from "../../../../../components/utilities/LocationPicker";
import DateTimePickerComponent from "../../../../../components/utilities/DateTimePickerComponent";
import MarkupTextEditor from "../../../../../components/utilities/MarkupTextEditor";
import HelpButton from "../../../../../components/utilities/HelpButton";
import ReoccurringEventModal from "../../SubComponents/ReoccurringEventModal";
import EventCardPreview from "../../../../../components/layout/eventcard/EventCardPreview";
import { getEnvironmentVariable } from "../../../../../global/environmentVariables";

const IS_UY = getEnvironmentVariable("IS_UY") === "true";

export default function EventCreationEventDetails({
  getInitialValues,
  getDetailsValidationSchema,
  submitDetails,
  setEventValues,
  eventValues,
  event,
  copyEvent,
  readOnly,
  gameStore,
  globalVars,
  authContext,
  eventSubType,
  gameSystem,
  locations,
  setLocations,
  setTimeZone,
  reoccurringOpen,
  setReoccurringOpen,
  eventId,
  EventStepper,
  step,
  setStep,
  circuits,
  buildPreviewEvent
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.eventCreation"
  });

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Grid item xs={12} sx={{ pt: 2 }}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={getDetailsValidationSchema()}
        onSubmit={submitDetails}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          setFieldTouched
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Event Creation Progress Stepper */}
            <EventStepper
              step={step}
              setStep={setStep}
              event={event}
              circuits={circuits}
            />

            <ConnectedFocusError />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  onBlur={() => {
                    setEventValues(
                      eventValues
                        ? { ...eventValues, ...values }
                        : { ...values }
                    );
                  }}
                >
                  <Grid item xs={12}>
                    <ImageUploader
                      values={values}
                      label={values.name}
                      name="eventImage"
                      setFieldValue={setFieldValue}
                      fileId={event?.photoFileId || copyEvent?.photoFileId}
                      size="large"
                      defaultUrl={
                        event?.photo?.url ||
                        event?.photoUrl ||
                        copyEvent?.photo?.url ||
                        copyEvent?.photoUrl
                      }
                      disabled={readOnly}
                    />
                  </Grid>
                  {Boolean(
                    event?.gameStore?.id ||
                      copyEvent?.gameStore?.id ||
                      gameStore?.id
                  ) && (
                    <Grid item xs={12}>
                      <TextField
                        name="gameStore"
                        label={t("organization")}
                        value={
                          event?.gameStore?.name ||
                          copyEvent?.gameStore?.name ||
                          gameStore?.name ||
                          ""
                        }
                        variant="outlined"
                        fullWidth
                        disabled
                      />
                    </Grid>
                  )}
                  {!IS_UY && (
                    <Grid item xs={12}>
                      <ButtonGroup fullWidth>
                        <Button
                          variant={
                            values?.isOnlineEvent ? "outlined" : "contained"
                          }
                          onClick={() => {
                            setFieldValue("isOnlineEvent", false);
                          }}
                          startIcon={
                            !values?.isOnlineEvent && <BCPCircleCheck />
                          }
                        >
                          {t("inPersonEvent")}
                        </Button>
                        <Button
                          variant={
                            values?.isOnlineEvent ? "contained" : "outlined"
                          }
                          onClick={() => {
                            setFieldValue("isOnlineEvent", true);
                          }}
                          startIcon={
                            values?.isOnlineEvent && <BCPCircleCheck />
                          }
                        >
                          {t("virtualEvent")}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  )}
                  {globalVars?.eventSubTypes?.list?.length > 0 && (
                    <Grid item xs={12}>
                      {authContext?.authState?.identity?.isAdmin === true ||
                      authContext?.authState?.identity?.adminGameSystems?.includes(
                        event?.gameSystemId
                      ) ? (
                        <GenericPicker
                          name="eventSubType"
                          label={
                            globalVars?.eventSubTypes?.label || "Event Sub Type"
                          }
                          value={values.eventSubType}
                          handleChange={handleChange}
                          list={globalVars?.eventSubTypes?.list}
                          inclAny={false}
                        />
                      ) : (
                        <TextField
                          name="eventSubType"
                          label={
                            globalVars?.eventSubTypes?.label || "Event Sub Type"
                          }
                          value={event?.eventSubType || eventSubType || ""}
                          variant="outlined"
                          fullWidth
                          disabled
                        />
                      )}
                    </Grid>
                  )}
                  {(event?.gameSystem?.eventFormats?.list?.length > 0 ||
                    copyEvent?.gameSystem?.eventFormats?.list?.length > 0 ||
                    gameSystem?.eventFormats?.list?.length > 0) && (
                    <Grid item xs={12}>
                      <GenericPicker
                        name="eventFormat"
                        label={
                          event?.gameSystem?.eventFormats?.label ||
                          copyEvent?.gameSystem?.eventFormats?.label ||
                          gameSystem?.eventFormats?.label ||
                          "Format"
                        }
                        value={values.eventFormat}
                        handleChange={handleChange}
                        list={
                          event?.gameSystem?.eventFormats?.list ||
                          copyEvent?.gameSystem?.eventFormats?.list ||
                          gameSystem?.eventFormats?.list ||
                          []
                        }
                        inclAny={false}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

            <Grid item xs={12} md={8}>
              <Grid
                container
                spacing={2}
                onBlur={() => {
                  setEventValues(
                    eventValues
                      ? { ...eventValues, ...values }
                      : { ...values }
                  );
                }}
              >
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label={t("eventName")}
                  value={values.name}
                  variant="outlined"
                  fullWidth
                  required
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={Boolean(touched.name && errors.name)}
                  helperText={
                    touched.name && errors.name
                      ? errors.name
                      : values.name?.length > 30
                        ? t("eventNameLengthWarning") ||
                          "For best display, keep the event name under 30 characters"
                        : null
                  }
                  disabled={readOnly}
                  autoComplete="off"
                  sx={{
                    ...(values.name?.length > 30 &&
                      !errors.name && {
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "warning.main"
                          },
                          "&:hover fieldset": {
                            borderColor: "warning.dark"
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "warning.main"
                          }
                        },
                        "& .MuiFormHelperText-root": {
                          color: "warning.main"
                        }
                      })
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <LocationPicker
                  locationId={values.locationId}
                  required
                  label={
                    values?.isOnlineEvent ? t("timeZoneLocation") : t("location")
                  }
                  locations={locations}
                  setLocations={setLocations}
                  setLocation={(location) => {
                    setFieldValue("location", location);
                    setFieldValue("locationId", location?.id);
                    setTimeZone(location?.timeZone);
                    var newLocations = [...locations];
                    var found = false;
                    newLocations.forEach((tempLocation) => {
                      if (tempLocation.id === location?.id) {
                        found = true;
                        tempLocation = {
                          ...tempLocation,
                          ...location,
                        };
                        return true;
                      }
                    });
                    if (!found) {
                      newLocations.push(location);
                    }
                    setLocations(newLocations);
                    setEventValues(
                      eventValues
                        ? { ...eventValues, ...values }
                        : { ...values }
                    );
                  }}
                  error={Boolean(touched.locationId && errors.locationId)}
                  helperText={touched.locationId && errors.locationId}
                  setFieldTouched={() => setFieldTouched("locationId")}
                  disabled={Boolean(readOnly)}
                />
              </Grid>

                  {values?.isOnlineEvent && (
                    <Grid item xs={12}>
                      <TextField
                        name="onlineEventLink"
                        label={t("virtualEventLink")}
                        value={values?.onlineEventLink}
                        variant="outlined"
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched.values?.onlineEventLink &&
                            errors.values?.onlineEventLink
                        )}
                        helperText={
                          touched.values?.onlineEventLink &&
                          errors.values?.onlineEventLink
                        }
                        disabled={readOnly}
                        autoComplete="off"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <DateTimePickerComponent
                      name="eventDate"
                      label={t("startDateTime")}
                      variant="outlined"
                      fullWidth
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      error={Boolean(touched.eventDate && errors.eventDate)}
                      helperText={touched.eventDate && errors.eventDate}
                      disabled={readOnly}
                      value={values.eventDate}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DateTimePickerComponent
                      name="eventEndDate"
                      label={t("endDateTime")}
                      fullWidth
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      error={Boolean(
                        touched.eventEndDate && errors.eventEndDate
                      )}
                      helperText={touched.eventEndDate && errors.eventEndDate}
                      disabled={readOnly}
                      value={values.eventEndDate}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MarkupTextEditor
                      name="eventDescriptionMarkup"
                      label={t("eventDescription")}
                      value={
                        values.eventDescriptionMarkup || values.eventDescription
                      }
                      onBlur={handleBlur}
                      onChange={({ text }) => {
                        setFieldValue("eventDescriptionMarkup", text);
                      }}
                      error={Boolean(
                        touched.eventDescriptionMarkup &&
                          errors.eventDescriptionMarkup
                      )}
                      helperText={
                        touched.eventDescriptionMarkup &&
                        errors.eventDescriptionMarkup
                      }
                      sx={{ height: "300px" }}
                      disabled={readOnly}
                    />
                  </Grid>

                  {!IS_UY && (
                    <Grid item xs={12}>
                      <TextField
                        name="externalUrl"
                        label={t("externalUrl")}
                        value={values.externalUrl}
                        variant="outlined"
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched.externalUrl && errors.externalUrl
                        )}
                        helperText={touched.externalUrl && errors.externalUrl}
                        disabled={readOnly}
                      />
                    </Grid>
                  )}

                  {authContext?.authState?.identity?.isAdmin && (
                    <>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            mt: 1,
                            mb: 1
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="700">
                          {t("adminSettings")}
                        </Typography>
                      </Grid>

                      {!IS_UY && (
                        <Grid item xs={12}>
                          <FormControlLabel
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                {t("leagueEvent")}{" "}
                                <HelpButton title={t("leagueEventHelp")} />
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)",
                              },
                            }}
                            control={
                              <Switch
                                color="primary"
                                checked={values.leagueEvent}
                                onChange={(e) => {
                                  setFieldValue(
                                    "leagueEvent",
                                    !values.leagueEvent
                                  );
                                }}
                                disabled={readOnly}
                              />
                            }
                          />
                        </Grid>
                      )}

                      {/* reoccurringEvents only available on new events */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={
                            <Typography className="MuiFormControlLabel-label">
                              {t("repeatEvent")}{" "}
                              <HelpButton title={t("repeatEventHelp")} />
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              color="primary"
                              checked={values.reoccurringEvent}
                              onChange={(e) => {
                                setFieldValue(
                                  "reoccurringEvent",
                                  !values.reoccurringEvent
                                );
                                setReoccurringOpen(!values.reoccurringEvent);
                              }}
                              disabled={!!eventId}
                            />
                          }
                        />
                      </Grid>

                      {values.reoccurringEvent && (
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setReoccurringOpen(true);
                            }}
                          >
                            {t("recurrenceDetails")}
                          </Button>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <FormControlLabel
                          label={
                            <Typography className="MuiFormControlLabel-label">
                              {t("sponsoredEvent")}{" "}
                              <HelpButton
                                title={`${t("sponsoredEventHelp")}${
                                  globalVars?.pricing?.subscriptions?.playerSub
                                    ? ` In addition the event's ${
                                        event?.gameSystem?.listLabel ||
                                        copyEvent?.gameSystem?.listLabel ||
                                        gameSystem?.listLabel ||
                                        "List"
                                      }s will be viewable to non subscribers`
                                    : ""
                                }`}
                              />
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              color="primary"
                              checked={values.sponsored}
                              onChange={(e) => {
                                let newSponsored = !values.sponsored;
                                setFieldValue("sponsored", newSponsored);
                                if (newSponsored) {
                                  setFieldValue("featured", newSponsored);
                                }
                              }}
                              disabled={readOnly}
                            />
                          }
                        />
                      </Grid>

                      {/* Only loads if reoccurringEvent is checked */}
                      <ReoccurringEventModal
                        values={values}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        reoccurringOpen={reoccurringOpen}
                        setReoccurringOpen={setReoccurringOpen}
                      />
                    </>
                  )}

                  {(authContext?.authState?.identity?.isAdmin === true ||
                    authContext?.authState?.identity?.adminGameSystems?.includes(
                      event?.gameSystemId
                    )) && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        label={
                          <Typography className="MuiFormControlLabel-label">
                            {t("featuredEvent")}{" "}
                            <HelpButton
                              title={t("featuredEventHelp")}
                            />
                          </Typography>
                        }
                        sx={{
                          ml: 0,
                          width: "100%",
                          "& .MuiFormControlLabel-label": {
                            width: "calc(100% - 58px)",
                          },
                        }}
                        control={
                          <Switch
                            color="primary"
                            checked={values.featured}
                            onChange={(e) => {
                              setFieldValue("featured", !values.featured);
                            }}
                            disabled={readOnly}
                          />
                        }
                      />
                    </Grid>
                  )}

              <Grid item xs={12}>
                <Button fullWidth variant="contained" type="submit">
                  {eventId ? t("saveChanges") : t("nextGameConfiguration")}
                </Button>
              </Grid>
            </Grid>
              </Grid>

              {isMdUp && (
                <Grid item md={4} sx={{ display: { xs: "none", md: "block" } }}>
                  <Box
                    sx={{
                      position: "sticky",
                      top: 145,
                      maxHeight: "calc(100vh - 120px)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", mb: 1, display: "block" }}
                    >
                      {t("eventCardPreview") || "Event Card Preview"}
                    </Typography>
                    <Box sx={{ flex: "0 1 auto", minHeight: 0 }}>
                      <EventCardPreview
                        event={buildPreviewEvent(values)}
                        preview={true}
                      />
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </form>
        )}
      </Formik>
    </Grid>
  );
}
